// Layar antrian untuk TABLET di ruang tunggu — PUBLIK, tanpa login.
//
// Keputusan owner 31 Agu 2026: rute publik per cabang, pola sama dengan
// `/csat/:token` dan `/faktur/:token` — token di URL adalah otorisasinya.
// Sengaja TIDAK memakai AuthContext/Layout: tablet ini bukan user aplikasi,
// dan satu import komponen ber-auth saja bisa memicu redirect ke /login.
//
// HALAMAN INI HANYA MENAMPILKAN. Tidak ada satu pun panggilan tulis di sini,
// dan endpoint yang dipakainya (`GET /antrian/public/display/{token}`) memang
// tidak punya pasangan tulis — dijaga tes
// `tests/test_antrian.py::TestDisplayPublik::test_semua_rute_publik_hanya_get`.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

// 5 detik: cukup cepat supaya pelanggan melihat nomornya berpindah sesaat
// setelah FL memanggil, cukup jarang supaya tablet murah tidak kepanasan.
const JEDA_SEGAR_MS = 5000;

function jamDari(iso) {
  // Jam diambil dari string ISO ber-offset WIB yang dikirim server, DIPOTONG
  // apa adanya — bukan `new Date(...)` yang akan menerjemahkannya ke zona
  // waktu tablet (tablet ruang tunggu sering tidak pernah di-set zamannya).
  return typeof iso === "string" && iso.length >= 16 ? iso.slice(11, 16) : "";
}

export default function AntrianDisplay() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [galat, setGalat] = useState("");

  const muat = useCallback(() => {
    api.get(`/antrian/public/display/${token}`)
      .then((r) => { setData(r.data); setGalat(""); })
      .catch((e) => {
        // Jangan menghapus tampilan yang sudah ada saat jaringan sekejap
        // putus — nomor yang hilang dari layar bikin panik ruang tunggu.
        // Pesan galat hanya menggantikan layar kalau memang belum ada data.
        setGalat(e?.response?.data?.detail || "Tidak bisa memuat data antrian");
      });
  }, [token]);

  useEffect(() => {
    muat();
    const t = setInterval(muat, JEDA_SEGAR_MS);
    return () => clearInterval(t);
  }, [muat]);

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-900 text-white grid place-items-center p-8">
        <p className="text-2xl" data-testid="antrian-display-status">
          {galat || "Memuat layar antrian…"}
        </p>
      </div>
    );
  }

  const dipanggil = data.sedang_dipanggil || [];
  const utama = dipanggil[0] || null;
  const lain = dipanggil.slice(1, 4);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      <header className="flex items-center justify-between px-8 py-5 border-b border-slate-700">
        <h1 className="text-3xl font-bold tracking-wide" data-testid="antrian-display-cabang">
          {data.cabang}
        </h1>
        <p className="text-2xl text-slate-300" data-testid="antrian-display-jam">
          {jamDari(data.waktu_server)} WIB
        </p>
      </header>

      <main className="flex-1 grid gap-6 p-8 lg:grid-cols-3">
        <section className="lg:col-span-2 rounded-3xl bg-emerald-600 flex flex-col items-center justify-center p-10">
          <p className="text-2xl uppercase tracking-[0.3em] text-emerald-100">Nomor dipanggil</p>
          <p className="font-bold leading-none my-6"
             style={{ fontSize: "clamp(5rem, 22vw, 16rem)" }}
             data-testid="antrian-display-nomor">
            {utama ? utama.nomor_antrian : "—"}
          </p>
          <p className="text-3xl text-emerald-50" data-testid="antrian-display-petugas">
            {utama ? (utama.fl_nama || "Silakan menuju petugas") : "Menunggu panggilan berikutnya"}
          </p>
        </section>

        <section className="space-y-6">
          {lain.length > 0 && (
            <div className="rounded-2xl bg-slate-800 p-6">
              <p className="text-lg uppercase tracking-widest text-slate-400 mb-3">Juga dilayani</p>
              <ul className="space-y-2" data-testid="antrian-display-juga-dilayani">
                {lain.map((t) => (
                  <li key={t.kode_antrian} className="flex items-baseline justify-between">
                    <span className="text-4xl font-semibold">{t.nomor_antrian}</span>
                    <span className="text-lg text-slate-400">{t.fl_nama}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-2xl bg-slate-800 p-6">
            <p className="text-lg uppercase tracking-widest text-slate-400 mb-3">
              Berikutnya ({data.total_menunggu} menunggu)
            </p>
            <ul className="flex flex-wrap gap-3" data-testid="antrian-display-berikutnya">
              {(data.berikutnya || []).length === 0 ? (
                <li className="text-2xl text-slate-500">Tidak ada antrean</li>
              ) : (data.berikutnya || []).slice(0, 8).map((t) => (
                <li key={t.kode_antrian}
                    className="px-4 py-2 rounded-xl bg-slate-700 text-3xl font-semibold">
                  {t.nomor_antrian}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <footer className="px-8 py-4 text-center text-slate-400 text-lg border-t border-slate-700">
        Ambil nomor antrian di meja depan · Terima kasih atas kesabaran Anda
      </footer>
    </div>
  );
}
